/**
 * 将持久化 TTS 音色规范为当前可选 Kokoro ID（仅设置入口需要；请求层直传 voice_type）
 */
import {
  DEFAULT_TTS_VOICE,
  LEARNING_MODULE_FIXED_US_VOICE,
  LEARNING_MODULE_FIXED_UK_VOICE,
  normalizeTtsVoice
} from '@/config/ttsVoices'
import {
  LEARNING_MODULE_USE_USER_TTS_SETTINGS,
  LEARNING_MODULE_FIXED_TTS
} from '@/config/learningModuleTts'
import { TTS_PROVIDER, DEFAULT_TTS_UI_SPEED, resolveEffectiveUiSpeed } from '@/utils/ttsRequest'

export { normalizeTtsVoice }

export const DEFAULT_TTS_PROVIDER = TTS_PROVIDER
export const DEFAULT_PLAYBACK_SPEAKER = DEFAULT_TTS_VOICE

/** 对齐 Demo：音量 UI 0～2 */
export const TTS_UI_VOLUME_MAX = 2
export const TTS_SEGMENT_PHONEME_VOLUME = TTS_UI_VOLUME_MAX

export function isSegmentPhonemeKind (kind) {
  return kind === 'grapheme' || kind === 'syllable'
}

export function resolveTtsPlaybackVolume (baseVolume, { segmentPhoneme = false } = {}) {
  if (segmentPhoneme) return TTS_SEGMENT_PHONEME_VOLUME
  const n = Number(baseVolume)
  const v = Number.isFinite(n) ? n : 1
  return Math.min(TTS_UI_VOLUME_MAX, Math.max(0, Math.round(v * 10) / 10))
}

export function resolveTtsPlaybackFromPersisted (_ttsProvider, speaker) {
  return {
    ttsProvider: TTS_PROVIDER,
    speaker: normalizeTtsVoice(speaker)
  }
}

/** @param {string} speaker @param {'uk'|'us'} locale */
export function resolveTtsVoiceForLocale (speaker, locale) {
  const s = normalizeTtsVoice(speaker)
  const lower = s.toLowerCase()
  if (locale === 'uk') {
    if (lower.startsWith('bf_') || lower.startsWith('bm_')) return s
    return LEARNING_MODULE_FIXED_UK_VOICE
  }
  if (lower.startsWith('af_') || lower.startsWith('am_')) return s
  return LEARNING_MODULE_FIXED_US_VOICE
}

function currentUserId () {
  try {
    const raw = localStorage.getItem('userInfo')
    const u = raw ? JSON.parse(raw) : null
    return u?.id ?? u?.userId ?? u?.user_id ?? ''
  } catch (_) {
    return ''
  }
}

export function playbackTtsFieldsFromApiSettings (settings = {}) {
  const rawSpeaker = settings.speaker !== null && settings.speaker !== undefined && settings.speaker !== ''
    ? String(settings.speaker)
    : ''
  const resolved = resolveTtsPlaybackFromPersisted(TTS_PROVIDER, rawSpeaker)
  return {
    ttsProvider: resolved.ttsProvider,
    speaker: resolved.speaker,
    speakerSpeed: resolveEffectiveUiSpeed(
      settings.speaker_speed !== undefined ? settings.speaker_speed : DEFAULT_TTS_UI_SPEED,
      currentUserId()
    ),
    speakerVolume: settings.speaker_volume !== undefined ? Number(settings.speaker_volume) : 1
  }
}

/** @returns {'uk'|'us'} */
export function resolveTtsLocaleFromSpeaker (speaker) {
  const s = String(speaker || '').trim().toLowerCase()
  if (s.startsWith('bf_') || s.startsWith('bm_') || s.startsWith('en-gb')) return 'uk'
  return 'us'
}

export function buildTtsRequestOptionsFromPlaybackSettings (settings = {}, locale = null) {
  const speed = resolveEffectiveUiSpeed(
    settings.speakerSpeed !== undefined ? settings.speakerSpeed : DEFAULT_TTS_UI_SPEED,
    currentUserId()
  )
  const volume = settings.speakerVolume !== undefined ? Number(settings.speakerVolume) : 1
  const resolved = resolveTtsPlaybackFromPersisted(settings.ttsProvider, settings.speaker)
  let voice_type = resolved.speaker
  if (locale) {
    voice_type = resolveTtsVoiceForLocale(resolved.speaker, locale)
  }
  return {
    ttsProvider: TTS_PROVIDER,
    voice_type,
    speed,
    volume: Number.isFinite(volume) ? volume : 1
  }
}

function resolveLearningModuleFixedVoice (locale = null) {
  const fixed = LEARNING_MODULE_FIXED_TTS
  const base = String(fixed.speaker || DEFAULT_TTS_VOICE).trim() || DEFAULT_TTS_VOICE
  if (!locale) return normalizeTtsVoice(base)
  const byLocale = fixed.speakerByLocale && fixed.speakerByLocale[locale]
  if (byLocale) return normalizeTtsVoice(byLocale)
  return resolveTtsVoiceForLocale(base, locale)
}

export function buildTtsRequestOptionsForLearningModules (settings = {}, locale = null) {
  if (LEARNING_MODULE_USE_USER_TTS_SETTINGS) {
    return buildTtsRequestOptionsFromPlaybackSettings(settings, locale)
  }
  const fixed = LEARNING_MODULE_FIXED_TTS
  const speed = fixed.speed !== undefined ? Number(fixed.speed) : DEFAULT_TTS_UI_SPEED
  const volume = fixed.volume !== undefined ? Number(fixed.volume) : 1
  return {
    ttsProvider: TTS_PROVIDER,
    voice_type: resolveLearningModuleFixedVoice(locale),
    speed: Number.isFinite(speed) ? speed : DEFAULT_TTS_UI_SPEED,
    volume: Number.isFinite(volume) ? volume : 1
  }
}

export function resolveLearningModuleTtsLocale (settings = {}) {
  if (LEARNING_MODULE_USE_USER_TTS_SETTINGS) {
    return resolveTtsLocaleFromSpeaker(settings.speaker)
  }
  return resolveTtsLocaleFromSpeaker(LEARNING_MODULE_FIXED_TTS.speaker)
}

import { syncWordLookupContentTypesFromApiData } from '@/utils/wordLookupContentSettings.js'

export async function hydratePlaybackSettingsToStore (store, { logTag = 'TTS' } = {}) {
  if (!store?.state?.earthworm) return
  try {
    const { getUserSettings } = await import('@/api/modules/user')
    const res = await getUserSettings({})
    if (res?.code !== 1 || !res.data) return
    const data = res.data
    store.commit('earthworm/SET_PLAYBACK_SETTINGS', {
      settings: playbackTtsFieldsFromApiSettings(data),
      silent: true
    })
    syncWordLookupContentTypesFromApiData(store, data)
  } catch (error) {
    console.warn(`[${logTag}] 加载播放设置失败:`, error)
  }
}
